<template>
  <div class="page-card">
    <div class="page-head">
      <h3>Athlete Profiles</h3>
      <el-button type="primary" size="small" @click="openCreate">New Profile</el-button>
    </div>

    <el-form :inline="true" :model="query" size="small" class="search-form">
      <el-form-item label="Keyword">
        <el-input v-model="query.keyword" clearable placeholder="Name / device / type / User ID" @keyup.enter.native="search" />
      </el-form-item>
      <el-form-item label="Sex">
        <el-select v-model="query.sex" clearable placeholder="All">
          <el-option label="Male" value="Male" />
          <el-option label="Female" value="Female" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">Search</el-button>
        <el-button @click="resetSearch">Reset</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="list" v-loading="loading" border size="small">
      <el-table-column prop="id" label="ID" width="70" />
      <el-table-column label="User ID" min-width="150">
        <template slot-scope="scope">{{ formatUserId(scope.row) }}</template>
      </el-table-column>
      <el-table-column prop="fullName" label="Name" min-width="120" />
      <el-table-column prop="age" label="Age" width="70" />
      <el-table-column prop="sex" label="Sex" width="90" />
      <el-table-column prop="workoutTypes" label="Training Types" min-width="160" show-overflow-tooltip />
      <el-table-column prop="sessionsPerWeek" label="Sessions/Week" width="110" />
      <el-table-column prop="trainingAge" label="Training Age" width="110" />
      <el-table-column prop="primaryDevice" label="Device" min-width="140" show-overflow-tooltip />
      <el-table-column prop="rhrBaseline" label="RHR Baseline" width="120" />
      <el-table-column prop="rhrSd" label="RHR SD" width="90" />
      <el-table-column label="Actions" width="150" fixed="right">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="openEdit(scope.row)">Edit</el-button>
          <el-button type="text" size="small" class="danger" @click="remove(scope.row)">Delete</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      background
      layout="total, sizes, prev, pager, next"
      :total="total"
      :page-size="query.pageSize"
      :current-page="query.pageNum"
      :page-sizes="[10, 20, 50, 100]"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />

    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="620px">
      <el-form :model="form" label-width="110px" size="small">
        <el-row :gutter="16">
          <el-col :span="12"><el-form-item label="User ID"><el-input-number v-model="form.userId" :min="1" style="width:100%" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="Name"><el-input v-model="form.fullName" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="Age"><el-input-number v-model="form.age" :min="0" style="width:100%" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="Sex"><el-select v-model="form.sex" style="width:100%"><el-option label="Male" value="Male" /><el-option label="Female" value="Female" /></el-select></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="Sessions/Week"><el-input-number v-model="form.sessionsPerWeek" :min="0" style="width:100%" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="Training Age"><el-input-number v-model="form.trainingAge" :min="0" style="width:100%" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="RHR Baseline"><el-input-number v-model="form.rhrBaseline" :min="0" style="width:100%" /></el-form-item></el-col>
          <el-col :span="12"><el-form-item label="RHR SD"><el-input-number v-model="form.rhrSd" :min="0" :step="0.1" style="width:100%" /></el-form-item></el-col>
        </el-row>
        <el-form-item label="Training Types"><el-input v-model="form.workoutTypes" placeholder="Strength,Running,HIIT" /></el-form-item>
        <el-form-item label="Experience"><el-input v-model="form.experience" placeholder="Beginner/Intermediate/Advanced" /></el-form-item>
        <el-form-item label="Training Days"><el-input v-model="form.trainingDays" placeholder="Mon,Wed,Fri" /></el-form-item>
        <el-form-item label="Check-in Time"><el-input v-model="form.checkinTime" placeholder="07:00" /></el-form-item>
        <el-form-item label="Recovery Focus"><el-input v-model="form.recoveryFocus" /></el-form-item>
        <el-form-item label="Primary Device"><el-input v-model="form.primaryDevice" /></el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible=false">Cancel</el-button>
        <el-button type="primary" @click="submit">Save</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { athleteApi } from '@/api/health'
import { formatUserId } from '@/utils/userDisplay'

const emptyForm = () => ({
  id: null,
  userId: null,
  fullName: '',
  age: null,
  sex: 'Male',
  workoutTypes: '',
  sessionsPerWeek: null,
  trainingAge: null,
  experience: '',
  trainingDays: '',
  checkinTime: '',
  recoveryFocus: '',
  primaryDevice: '',
  rhrBaseline: null,
  rhrSd: null,
  aceInhibitor: false,
  betaBlocker: false
})

export default {
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      query: { pageNum: 1, pageSize: 10, keyword: '', sex: '' },
      dialogVisible: false,
      dialogTitle: 'New Profile',
      form: emptyForm()
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    formatUserId,
    async loadData() {
      this.loading = true
      try {
        const res = await athleteApi.list(this.query)
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    search() {
      this.query.pageNum = 1
      this.loadData()
    },
    resetSearch() {
      this.query = { pageNum: 1, pageSize: 10, keyword: '', sex: '' }
      this.loadData()
    },
    handleSizeChange(size) {
      this.query.pageSize = size
      this.query.pageNum = 1
      this.loadData()
    },
    handleCurrentChange(page) {
      this.query.pageNum = page
      this.loadData()
    },
    openCreate() {
      this.dialogTitle = 'New Profile'
      this.form = emptyForm()
      this.dialogVisible = true
    },
    openEdit(row) {
      this.dialogTitle = 'Edit Profile'
      this.form = { ...emptyForm(), ...row }
      this.dialogVisible = true
    },
    async submit() {
      if (this.form.id) await athleteApi.update(this.form)
      else await athleteApi.create(this.form)
      this.$message.success('Saved successfully')
      this.dialogVisible = false
      this.loadData()
    },
    async remove(row) {
      await this.$confirm(`Delete profile ${row.fullName || row.id}?`, 'Confirm', { type: 'warning' })
      await athleteApi.remove(row.id)
      this.$message.success('Deleted successfully')
      this.loadData()
    }
  }
}
</script>

<style scoped>
.page-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.page-head h3 { margin: 0; }
.search-form { margin-bottom: 12px; }
.pager { margin-top: 16px; text-align: right; }
.danger { color: #f56c6c; }
</style>
